// useHeaderAnimations.js - Custom Hook for Header Animations
import { useEffect, useState, useCallback } from 'react';

export const useHeaderAnimations = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [cartPulse, setCartPulse] = useState(false);

  // Debounce function pour optimiser les performances
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  };

  // Gestion du scroll avec debounce
  const handleScroll = useCallback(
    debounce(() => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);
    }, 10),
    []
  );

  // Initialiser les écouteurs d'événements
  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  // Animation du compteur de panier
  const triggerCartPulse = useCallback(() => {
    setCartPulse(true);
    setTimeout(() => setCartPulse(false), 600);
  }, []);

  // Style dynamique pour le header
  const headerStyle = {
    height: isScrolled ? '70px' : '85px',
    background: isScrolled 
      ? 'linear-gradient(135deg, rgba(0, 115, 153, 0.95), rgba(0, 90, 115, 0.95))'
      : 'linear-gradient(135deg, #007399, #005a73)',
    boxShadow: isScrolled 
      ? '0 8px 32px rgba(0, 115, 153, 0.3)'
      : '0 4px 20px rgba(0, 115, 153, 0.3)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  };

  return {
    isScrolled,
    isSearchFocused,
    setIsSearchFocused,
    cartPulse,
    triggerCartPulse,
    headerStyle,
  };
};

// Hook pour les animations d'apparition
export const useScrollAnimation = (threshold = 0.1) => {
  const [isVisible, setIsVisible] = useState(false);
  const [element, setElement] = useState(null);

  useEffect(() => {
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(element);
        }
      },
      {
        threshold,
        rootMargin: '0px 0px -50px 0px',
      }
    );

    observer.observe(element);

    return () => {
      if (element) observer.unobserve(element);
    };
  }, [element, threshold]);

  return [setElement, isVisible];
};

// Hook pour l'effet ripple
export const useRipple = () => {
  const createRipple = useCallback((event) => {
    const button = event.currentTarget;
    const ripple = document.createElement('span');
    const rect = button.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = event.clientX - rect.left - size / 2;
    const y = event.clientY - rect.top - size / 2;

    ripple.style.cssText = `
      position: absolute;
      width: ${size}px;
      height: ${size}px;
      left: ${x}px;
      top: ${y}px;
      background: rgba(255, 255, 255, 0.5);
      border-radius: 50%;
      transform: scale(0);
      animation: ripple-animation 0.6s ease-out;
      pointer-events: none;
      z-index: 9999;
    `;

    const existingRipple = button.querySelector('.ripple-effect');
    if (existingRipple) {
      existingRipple.remove();
    }

    ripple.classList.add('ripple-effect');
    button.style.position = 'relative';
    button.style.overflow = 'hidden';
    button.appendChild(ripple);

    setTimeout(() => ripple.remove(), 600);
  }, []);

  return createRipple;
};

// Hook pour lazy loading des images
export const useLazyLoad = () => {
  const [ref, setRef] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!ref) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const img = entry.target;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
            img.onload = () => setIsLoaded(true);
          }
          observer.unobserve(img);
        }
      },
      { threshold: 0.01 }
    );

    observer.observe(ref);

    return () => {
      if (ref) observer.unobserve(ref);
    };
  }, [ref]);

  return [setRef, isLoaded];
};

// Hook pour les particules flottantes
export const useFloatingParticles = (containerRef, count = 20) => {
  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const particles = [];

    for (let i = 0; i < count; i++) {
      const particle = document.createElement('div');
      particle.className = 'floating-particle';
      particle.style.cssText = `
        position: absolute;
        width: ${Math.random() * 4 + 2}px;
        height: ${Math.random() * 4 + 2}px;
        background: rgba(0, 168, 204, ${Math.random() * 0.5 + 0.2});
        border-radius: 50%;
        left: ${Math.random() * 100}%;
        top: ${Math.random() * 100}%;
        pointer-events: none;
        animation: float ${Math.random() * 10 + 10}s ease-in-out infinite;
        animation-delay: ${Math.random() * 5}s;
        z-index: 0;
      `;
      container.appendChild(particle);
      particles.push(particle);
    }

    // Cleanup
    return () => {
      particles.forEach(p => p.remove());
    };
  }, [containerRef, count]);
};

// Hook pour smooth scroll
export const useSmoothScroll = () => {
  const scrollToElement = useCallback((elementId, offset = 0) => {
    const element = document.getElementById(elementId);
    if (!element) return;

    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  }, []);

  return scrollToElement;
};

// Hook pour les animations staggered (progressives)
export const useStaggeredAnimation = (itemsCount, delay = 100) => {
  const [visibleItems, setVisibleItems] = useState([]);

  useEffect(() => {
    const timers = [];
    for (let i = 0; i < itemsCount; i++) {
      const timer = setTimeout(() => {
        setVisibleItems(prev => [...prev, i]);
      }, i * delay);
      timers.push(timer);
    }

    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, [itemsCount, delay]);

  const isVisible = useCallback(
    (index) => visibleItems.includes(index),
    [visibleItems]
  );

  return isVisible;
};

// Hook pour détecter la navigation au clavier (accessibilité)
export const useKeyboardNav = () => {
  const [isKeyboardNav, setIsKeyboardNav] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Tab') {
        setIsKeyboardNav(true);
      }
    };

    const handleMouseDown = () => {
      setIsKeyboardNav(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);

  return isKeyboardNav;
};

// Hook pour les animations au hover
export const useHoverAnimation = () => {
  const [isHovered, setIsHovered] = useState(false);

  const hoverProps = {
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
  };

  const hoverStyle = {
    transform: isHovered ? 'translateY(-2px) scale(1.05)' : 'translateY(0) scale(1)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  };

  return { isHovered, hoverProps, hoverStyle };
};

// Export all hooks
export default {
  useHeaderAnimations,
  useScrollAnimation,
  useRipple,
  useLazyLoad,
  useFloatingParticles,
  useSmoothScroll,
  useStaggeredAnimation,
  useKeyboardNav,
  useHoverAnimation,
};